// Inspector drawer — slides in from the right inside a deck. Shows
// the same diagnostic payload the admin Inspector tab does (composed
// CLAUDE.md, tulpa+engine_config, R2 listing, recent activity +
// messages) for the current deck. Admin-only — the backend
// /api/admin/decks/:id/inspect is withAdmin-gated, so non-admin
// users will get a 403; the trigger button is also hidden unless
// isAdmin.
//
// The shared component pattern across the codebase is a fixed-overlay
// + slide-in panel, same as ShareDialog / MembersDialog / BrandDialog.
// We mirror that — backdrop click closes, Esc closes, scroll inside
// the drawer is independent.

import React, { useCallback, useEffect, useState } from 'react';
import { X, ArrowsClockwise } from '@phosphor-icons/react';
import { apiFetch } from '../api/auth-fetch';
import InlineFileViewer, { type FileContent } from './InlineFileViewer';

interface InspectorDrawerProps {
  deckId: string;
  open: boolean;
  onClose: () => void;
}

interface InspectorData {
  deck: Record<string, unknown>;
  workspace: Record<string, unknown> | null;
  tulpa: Record<string, unknown> | null;
  engine_config: Record<string, unknown>;
  workdir_path: string;
  r2_objects: { key: string; size: number; uploaded: number }[];
  r2_total_size: number;
  recent_messages: { role: string; agent?: string; content: string; created_at?: number }[];
  recent_activity: { kind: string; payload?: Record<string, unknown>; created_at: number }[];
  composed_claude_md: string;
  compose_error: string | null;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtTs(n: number): string {
  return n ? new Date(n).toLocaleString() : '-';
}

export default function InspectorDrawer({ deckId, open, onClose }: InspectorDrawerProps) {
  const [data, setData] = useState<InspectorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // File viewer state — populated when user clicks an R2 row.
  const [openFileKey, setOpenFileKey] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState('');

  const openFile = useCallback(async (key: string) => {
    if (openFileKey === key) {
      // Toggle off — collapse the viewer if user clicked the open row.
      setOpenFileKey(null);
      setFileContent(null);
      setFileError('');
      return;
    }
    setOpenFileKey(key);
    setFileContent(null);
    setFileError('');
    setFileLoading(true);
    try {
      const res = await apiFetch(`/api/decks/${deckId}/files?key=${encodeURIComponent(key)}`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const j = (await res.json()) as FileContent;
      setFileContent(j);
    } catch (e: any) {
      setFileError(e?.message || 'Failed to load file.');
    } finally {
      setFileLoading(false);
    }
  }, [deckId, openFileKey]);

  const load = useCallback(async () => {
    if (!deckId) return;
    setLoading(true);
    setError('');
    try {
      // Path moved from /api/admin/decks/:id/inspect to /api/decks/:id/inspect
      // when the auth scope expanded to admin OR deck owner. Server still
      // accepts the old admin-prefixed path as an alias for AdminDashboard.
      const res = await apiFetch(`/api/decks/${deckId}/inspect`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const j = (await res.json()) as InspectorData;
      setData(j);
    } catch (e: any) {
      setError(e?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  // Open → fetch fresh data. Closing doesn't reset so reopening shows
  // the prior snapshot until the user clicks Refresh.
  useEffect(() => {
    if (open && !data && !loading) {
      void load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deckId]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const backdrop: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 50,
    background: 'var(--ds-overlay)', backdropFilter: 'blur(2px)',
    transition: 'opacity 0.15s',
  };
  const panel: React.CSSProperties = {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 51,
    width: 'min(720px, 92vw)',
    background: 'var(--bg-surface)',
    borderLeft: '1px solid var(--border)',
    boxShadow: '-12px 0 32px var(--ds-shadow-color)',
    display: 'flex', flexDirection: 'column',
  };
  const header: React.CSSProperties = {
    flexShrink: 0, padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'var(--bg-surface)',
  };
  const body: React.CSSProperties = {
    flex: 1, overflow: 'auto', padding: '16px 20px',
  };
  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '12px 14px', marginBottom: 12,
  };
  const h2: React.CSSProperties = {
    fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-primary)',
    margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em',
  };
  const pre: React.CSSProperties = {
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    borderRadius: 6, padding: 10, fontSize: 'var(--fs-2xs)',
    fontFamily: 'monospace', overflow: 'auto', whiteSpace: 'pre',
    maxHeight: 320, margin: 0, color: 'var(--text-primary)',
  };
  const btnIcon: React.CSSProperties = {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 6, borderRadius: 6, color: 'var(--text-muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <>
      <div style={backdrop} onClick={onClose} />
      <aside style={panel}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
              Inspector
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
              {deckId}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => void load()} title="Refresh" style={btnIcon} disabled={loading}>
              <ArrowsClockwise size={16} weight="light" />
            </button>
            <button onClick={onClose} title="Close" style={btnIcon}>
              <X size={16} weight="light" />
            </button>
          </div>
        </div>
        <div style={body}>
          {loading && !data && (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
          )}
          {error && (
            <div style={{
              padding: 10, background: 'var(--ds-danger-soft)',
              border: '1px solid var(--ds-danger-soft)', borderRadius: 6,
              color: 'var(--ds-danger)', fontSize: 'var(--fs-xs)', marginBottom: 12,
            }}>{error}</div>
          )}

          {data && (
            <>
              {/* Deck */}
              <div style={sectionStyle}>
                <h2 style={h2}>Deck</h2>
                <table style={{ fontSize: 'var(--fs-xs)', width: '100%' }}>
                  <tbody>
                    {[
                      ['Phase', data.deck.phase],
                      ['Mode', data.deck.mode ?? '(unset)'],
                      ['Current version', data.deck.current_version ?? '(none)'],
                      ['Active task', data.deck.active_task ?? '(none)'],
                      ['Tulpa ID (bound)', data.deck.tulpa_id ?? '(none — default)'],
                      ['Workspace', data.workspace?.name ?? data.deck.workspace_id],
                      ['Workdir (sandbox)', data.workdir_path],
                    ].map(([k, v]) => (
                      <tr key={String(k)}>
                        <td style={{ padding: '3px 12px 3px 0', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{String(k)}</td>
                        <td style={{ padding: '3px 0', fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v ?? '-')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Tulpa + engine */}
              <div style={sectionStyle}>
                <h2 style={h2}>Tulpa + engine</h2>
                {data.tulpa ? (
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', marginBottom: 6 }}>
                    <code style={{ color: 'var(--text-primary)' }}>{String(data.tulpa.slug)}</code> — {String(data.tulpa.name)}
                    {data.tulpa.workspace_id ? ' (workspace)' : ' (system default)'}
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>No tulpa (fallback fired).</div>
                )}
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                    engine_config
                  </summary>
                  <pre style={{ ...pre, marginTop: 6 }}>{JSON.stringify(data.engine_config, null, 2)}</pre>
                </details>
              </div>

              {/* Composed CLAUDE.md */}
              <div style={sectionStyle}>
                <h2 style={h2}>Composed CLAUDE.md</h2>
                {data.compose_error && (
                  <div style={{ color: 'var(--ds-danger)', fontSize: 'var(--fs-xs)', marginBottom: 6 }}>
                    Compose error: {data.compose_error}
                  </div>
                )}
                <pre style={{ ...pre, maxHeight: 360 }}>{data.composed_claude_md || '(empty)'}</pre>
                <div style={{ marginTop: 4, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                  {data.composed_claude_md.length.toLocaleString()} chars
                </div>
              </div>

              {/* R2 storage — keys are clickable. Click → inline file
                  viewer below the table (text/json/svg inline, image
                  preview, binary download). Read-only in Phase 1. */}
              <div style={sectionStyle}>
                <h2 style={h2}>
                  R2 storage
                  <span style={{ marginLeft: 8, fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none' }}>
                    {data.r2_objects.length} obj · {fmtSize(data.r2_total_size)}
                  </span>
                </h2>
                {data.r2_objects.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>(no objects)</div>
                ) : (
                  <div style={{ ...pre, maxHeight: 280, padding: 0 }}>
                    <table style={{ width: '100%', fontSize: 'var(--fs-2xs)', fontFamily: 'monospace', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-sidebar)' }}>
                          <th style={{ padding: '4px 8px', textAlign: 'left' }}>Key</th>
                          <th style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.r2_objects.map((o) => {
                          const isOpen = openFileKey === o.key;
                          const shortKey = o.key.replace(`decks/${String(data.deck.id)}/`, '');
                          return (
                            <tr
                              key={o.key}
                              onClick={() => openFile(o.key)}
                              style={{ cursor: 'pointer', background: isOpen ? 'var(--bg-sidebar)' : undefined }}
                            >
                              <td style={{ padding: '3px 8px', borderTop: '1px solid var(--border)', color: isOpen ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                                {isOpen ? '▾ ' : '▸ '}{shortKey}
                              </td>
                              <td style={{ padding: '3px 8px', borderTop: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {fmtSize(o.size)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {openFileKey && (
                  <div style={{ marginTop: 10 }}>
                    <InlineFileViewer
                      deckId={String(data.deck.id)}
                      r2Key={openFileKey}
                      onClose={() => { setOpenFileKey(null); setFileContent(null); setFileLoading(false); setFileError(''); }}
                      content={fileContent}
                      loading={fileLoading}
                      error={fileError}
                      onSaved={(newEtag, newSize) => {
                        setFileContent((c) => (
                          c && c.kind === 'text' ? { ...c, etag: newEtag, size: newSize } : c
                        ));
                        // Refresh outer inspector so the R2 listing picks up the new size / uploaded time.
                        load();
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Recent activity */}
              <div style={sectionStyle}>
                <h2 style={h2}>
                  Recent activity
                  <span style={{ marginLeft: 8, fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none' }}>
                    {data.recent_activity.length}
                  </span>
                </h2>
                {data.recent_activity.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>(none)</div>
                ) : (
                  <pre style={pre}>{data.recent_activity.map((a) => {
                    const t = fmtTs(a.created_at).slice(11, 19); // HH:MM:SS only
                    const payloadStr = a.payload ? JSON.stringify(a.payload) : '';
                    return `${t}  ${a.kind.padEnd(18)}  ${payloadStr.slice(0, 80)}`;
                  }).join('\n')}</pre>
                )}
              </div>

              {/* Recent messages */}
              <div style={sectionStyle}>
                <h2 style={h2}>
                  Recent messages
                  <span style={{ marginLeft: 8, fontSize: 'var(--fs-2xs)', fontWeight: 400, color: 'var(--text-muted)', textTransform: 'none' }}>
                    {data.recent_messages.length}
                  </span>
                </h2>
                {data.recent_messages.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>(none)</div>
                ) : (
                  <pre style={{ ...pre, maxHeight: 360 }}>{data.recent_messages.map((m) => {
                    const role = `[${m.role}${m.agent ? `:${m.agent}` : ''}]`;
                    return `${role}\n${(m.content || '').slice(0, 400)}`;
                  }).join('\n\n')}</pre>
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

